/**
 * Client-side helpers for approval entanglement rules (mirror backend approvalValidation.js).
 */

import type { PlanningAction, ProjectPlan } from '../types';

function findTaskIdForAction(
  plan: ProjectPlan,
  actionId: number
): number | null {
  for (const phase of plan.phases) {
    for (const task of phase.tasks || []) {
      for (const a of task.actions || []) {
        if (a.actionId === actionId) {
          return task.taskId;
        }
      }
    }
  }
  return null;
}

export function buildActionIndexMap(plan: ProjectPlan): Map<number, number> {
  let idx = 0;
  const map = new Map<number, number>();
  const phases = [...plan.phases].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );
  for (const phase of phases) {
    const tasks = [...(phase.tasks || [])].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );
    for (const task of tasks) {
      const actions = [...(task.actions || [])].sort(
        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
      );
      for (const action of actions) {
        map.set(action.actionId, idx++);
      }
    }
  }
  return map;
}

export function getApproveActionsInOrder(plan: ProjectPlan): PlanningAction[] {
  const out: PlanningAction[] = [];
  const phases = [...plan.phases].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );
  for (const phase of phases) {
    const tasks = [...(phase.tasks || [])].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );
    for (const task of tasks) {
      const actions = [...(task.actions || [])].sort(
        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
      );
      for (const action of actions) {
        if (action.actionType === 'APPROVE') {
          out.push(action);
        }
      }
    }
  }
  return out;
}

/**
 * Reject target for APPROVE must be strictly after previous APPROVE in plan order and strictly before this APPROVE.
 */
function isSerialGateConsecutivePairUi(
  prev: PlanningAction,
  cur: PlanningAction,
  prevTaskId: number | null,
  curTaskId: number | null
): boolean {
  if (prevTaskId == null || curTaskId !== prevTaskId) {
    return false;
  }
  const ps = prev.sourceActionId;
  const cs = cur.sourceActionId;
  const pr = (prev as { rejectActionId?: number | null }).rejectActionId;
  const cr = (cur as { rejectActionId?: number | null }).rejectActionId;
  if (ps == null || cs == null || pr == null || cr == null) {
    return false;
  }
  return Number(ps) === Number(cs) && Number(pr) === Number(cr);
}

export function isRejectTargetAllowed(
  plan: ProjectPlan,
  approveActionId: number,
  rejectCandidateActionId: number
): boolean {
  const indexMap = buildActionIndexMap(plan);
  const curIdx = indexMap.get(approveActionId);
  const rejIdx = indexMap.get(rejectCandidateActionId);
  if (curIdx === undefined || rejIdx === undefined) {
    return false;
  }
  if (rejIdx >= curIdx) {
    return false;
  }
  const ordered = getApproveActionsInOrder(plan);
  const oi = ordered.findIndex((a) => a.actionId === approveActionId);
  if (oi <= 0) {
    return true;
  }
  const prev = ordered[oi - 1];
  const cur = ordered[oi];
  const prevTaskId = findTaskIdForAction(plan, prev.actionId);
  const curTaskId = findTaskIdForAction(plan, cur.actionId);
  const prevRej = (prev as { rejectActionId?: number | null }).rejectActionId;
  const curSrc = cur.sourceActionId;
  const prevSrc = prev.sourceActionId;
  const serialSharedReject =
    prevTaskId != null &&
    curTaskId === prevTaskId &&
    prevRej != null &&
    Number(prevRej) === Number(rejectCandidateActionId) &&
    (curSrc == null ||
      prevSrc == null ||
      Number(curSrc) === Number(prevSrc));
  if (serialSharedReject) {
    return true;
  }
  const prevApproveIdx = indexMap.get(prev.actionId);
  if (prevApproveIdx === undefined) {
    return true;
  }
  return rejIdx > prevApproveIdx;
}

export function sourceUsedByAnotherApprove(
  plan: ProjectPlan,
  currentActionId: number,
  sourceActionId: number
): boolean {
  const currentTaskId = findTaskIdForAction(plan, currentActionId);
  for (const phase of plan.phases) {
    for (const task of phase.tasks || []) {
      for (const a of task.actions || []) {
        if (a.actionType !== 'APPROVE') {
          continue;
        }
        if (a.actionId === currentActionId) {
          continue;
        }
        const src = a.sourceActionId;
        if (src != null && Number(src) === Number(sourceActionId)) {
          if (currentTaskId != null && task.taskId === currentTaskId) {
            continue;
          }
          return true;
        }
      }
    }
  }
  return false;
}

export function countApproveInTask(
  plan: ProjectPlan,
  taskId: number,
  excludeActionId?: number
): number {
  let n = 0;
  for (const phase of plan.phases) {
    for (const task of phase.tasks || []) {
      if (task.taskId !== taskId) {
        continue;
      }
      for (const a of task.actions || []) {
        if (a.actionType !== 'APPROVE') {
          continue;
        }
        if (excludeActionId != null && a.actionId === excludeActionId) {
          continue;
        }
        n++;
      }
    }
  }
  return n;
}

export interface RollbackSegment {
  approveActionId: number;
  rejectActionId: number;
  approveRowId: string;
  rejectRowId: string;
  valid: boolean;
}

export function buildRollbackSegments(plan: ProjectPlan): RollbackSegment[] {
  const segs: RollbackSegment[] = [];
  const ordered = getApproveActionsInOrder(plan);
  const indexMap = buildActionIndexMap(plan);
  for (let i = 0; i < ordered.length; i++) {
    const a = ordered[i];
    const rej = (a as { rejectActionId?: number | null }).rejectActionId;
    if (rej == null) {
      continue;
    }
    const curIdx = indexMap.get(a.actionId);
    const rejIdx = indexMap.get(Number(rej));
    let valid = curIdx !== undefined && rejIdx !== undefined && rejIdx < curIdx;
    if (valid && i > 0 && rejIdx !== undefined) {
      const prev = ordered[i - 1];
      const cur = ordered[i];
      const prevTaskId = findTaskIdForAction(plan, prev.actionId);
      const curTaskId = findTaskIdForAction(plan, cur.actionId);
      if (
        isSerialGateConsecutivePairUi(prev, cur, prevTaskId, curTaskId) &&
        Number((prev as { rejectActionId?: number | null }).rejectActionId) ===
          Number(rej)
      ) {
        valid = true;
      } else {
        const prevIdx = indexMap.get(prev.actionId);
        valid = prevIdx !== undefined && rejIdx > prevIdx;
      }
    }
    segs.push({
      approveActionId: a.actionId,
      rejectActionId: Number(rej),
      approveRowId: `action-${a.actionId}`,
      rejectRowId: `action-${Number(rej)}`,
      valid,
    });
  }
  return segs;
}

/** 2D segment intersection (inclusive endpoints). */
function segmentsIntersect(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number
): boolean {
  const o1 = orient(ax, ay, bx, by, cx, cy);
  const o2 = orient(ax, ay, bx, by, dx, dy);
  const o3 = orient(cx, cy, dx, dy, ax, ay);
  const o4 = orient(cx, cy, dx, dy, bx, by);
  if (o1 !== o2 && o3 !== o4) {
    return true;
  }
  return false;
}

function orient(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number
): number {
  const v = (by - ay) * (cx - bx) - (bx - ax) * (cy - by);
  if (Math.abs(v) < 1e-9) {
    return 0;
  }
  return v > 0 ? 1 : 2;
}

export function rollbackSegmentsCross(
  s1: { x1: number; y1: number; x2: number; y2: number },
  s2: { x1: number; y1: number; x2: number; y2: number }
): boolean {
  return segmentsIntersect(s1.x1, s1.y1, s1.x2, s1.y2, s2.x1, s2.y1, s2.x2, s2.y2);
}
